import { Controller } from '@hotwired/stimulus'
import { setDecimals } from '../../../../javascript/utils/math_helpers'
import { arrayWithoutElement } from '../../../../javascript/utils/array_helpers'

export default class extends Controller {
  static values = {
    isQuartersEnabled: Boolean,
    decimalCount: Number,
    lastHourRowIndex: Number,
    lastQuarterRowIndex: Number
  }
  static outlets = [
    'kaia--accordion-component',
    'kaia--manual-forecast--table-component--hours',
    'kaia--manual-forecast--toolbar-component',
    'kaia--manual-forecast--popup-component'
  ]

  selectedInputs = []
  selectedLocks = []

  ROW_INDEX_ATTR = 'data-kaia--manual-forecast--table-component-row-index-param'
  CELL_INDEX_ATTR =
    'data-kaia--manual-forecast--table-component-cell-index-param'
  HOURS_TARGET_ATTR =
    'data-kaia--manual-forecast--table-component--hours-target'
  SAVE_REMINDER_WARNING_MSG_INDEX = 0
  NON_CONTINUOUS_ERROR_MSG_INDEX = 1
  MISSING_VALUES_ERROR_MSG_INDEX = 2

  showAllQuarters() {
    this.kaiaAccordionComponentOutlets.forEach((accordion) => accordion.open())
  }

  hideAllQuarters() {
    this.kaiaAccordionComponentOutlets.forEach((accordion) => accordion.close())
  }

  toggleAll(event) {
    this.hoursOutlets.forEach((hoursOutlet) => {
      hoursOutlet.toggleHourWithQuarters(
        event.params.index,
        event.target.checked
      )
    })
  }

  handleCheckboxChange(event) {
    this.#updateSelectedInputs(
      event.params.rowIndex,
      event.params.cellIndex,
      event.target.checked
    )
    this.toolbarOutlet.selectedAmountValue = this.nonDummySelectedInputs.length
  }

  handleDistributeValues() {
    if (!this.#hasChronologicallySelectedInputs)
      return this.#showPopup(this.NON_CONTINUOUS_ERROR_MSG_INDEX)

    this.#distributeValues()
  }

  handleCopyValues() {
    if (!navigator.clipboard) return

    const clipboardItemData = { ['text/html']: this.selectedTable.outerHTML }
    const clipboardItem = new ClipboardItem(clipboardItemData)
    navigator.clipboard.write([clipboardItem])
  }

  handleDuplicateFirstValue() {
    this.#duplicateFirstValue()
  }

  handleDeleteValues() {
    // unlock values first
    this.alterSelectedLocks(false)
    this.selectedInputs.forEach((input) => this.#setInputValue(input, ''))

    this.#showPopup(this.SAVE_REMINDER_WARNING_MSG_INDEX)
  }

  getSelectedLocks() {
    return this.selectedLocks.filter(
      (lock, index) => this.selectedInputs[index].value
    )
  }

  alterSelectedLocks(state) {
    const selectedLocks = this.getSelectedLocks()
    const anyUnselected =
      typeof state == 'undefined'
        ? selectedLocks.some((i) => !i.checked)
        : state
    selectedLocks.forEach((lock) => {
      const icon =
        this.lockIcons[parseInt(lock.getAttribute(this.ROW_INDEX_ATTR))][
          parseInt(lock.getAttribute(this.CELL_INDEX_ATTR))
        ]
      lock.checked = anyUnselected
      icon.classList.toggle('invisible', !lock.checked)
      lock.dispatchEvent(new Event('change'))
    })
    return selectedLocks
  }

  lockValues() {
    // start by ignoring any selected without values if any unselected
    const affectedLocks = this.alterSelectedLocks()
    this.toolbarOutlet.allLockedValue =
      affectedLocks.length && affectedLocks.every((al) => al.checked)
    if (affectedLocks.length)
      this.#showPopup(this.SAVE_REMINDER_WARNING_MSG_INDEX)
  }

  handlePasteInput(event) {
    const pastedTable = new DOMParser()
      .parseFromString(event.clipboardData.getData('text/html'), 'text/html')
      .querySelector('table')
    if (!pastedTable) return

    const startPos = [event.params.rowIndex, event.params.cellIndex]
    const pastedInQuarterly =
      event.target.getAttribute(this.HOURS_TARGET_ATTR) === 'quarterVolume'
    // If pasting into hourly input when quarters enabled, start filling in from the hour's first quarter
    if (!pastedInQuarterly && this.isQuartersEnabledValue) startPos[0] *= 4
    // The setting of values must be done after paste event finishes. This puts it to bottom of the stack
    setTimeout(() => this.#fillInputs(pastedTable, startPos), 0)
  }

  #setInputValue(input, value) {
    input.value = value
    input.dispatchEvent(new Event('input')) // dispatch input event so calculations are performed
  }

  #updateSelectedInputs(rowIndex, cellIndex, isChecked) {
    const selectedInput = this.inputs[rowIndex][cellIndex]
    const selectedLock = this.locks[rowIndex][cellIndex]

    if (isChecked) {
      // Add selected input to array at correct index
      const indexOfNextSelectedInput = this.selectedInputs.findIndex(
        (input) => {
          const hasLaterDate =
            parseInt(input.getAttribute(this.CELL_INDEX_ATTR)) > cellIndex
          const isLaterOnSameDate =
            parseInt(input.getAttribute(this.CELL_INDEX_ATTR)) === cellIndex &&
            parseInt(input.getAttribute(this.ROW_INDEX_ATTR)) > rowIndex
          return hasLaterDate || isLaterOnSameDate
        }
      )
      const indexToInsertAt =
        indexOfNextSelectedInput === -1
          ? this.selectedInputs.length
          : indexOfNextSelectedInput
      this.selectedInputs.splice(indexToInsertAt, 0, selectedInput)
      this.selectedLocks.splice(indexToInsertAt, 0, selectedLock)
    } else {
      // Remove de-selected input from array
      this.selectedInputs = arrayWithoutElement(
        this.selectedInputs,
        selectedInput
      )
      this.selectedLocks = arrayWithoutElement(this.selectedLocks, selectedLock)
    }
    const applicableSelected = this.selectedLocks.filter(
      (lock, index) => this.selectedInputs[index].value
    )
    this.toolbarOutlet.allLockedValue = applicableSelected.length
      ? !applicableSelected.map((i) => i.checked).includes(false)
      : false
    selectedInput.setAttribute('selected', isChecked)
  }

  #distributeValues() {
    const inputsToFill = this.nonDummySelectedInputs

    const startValue = parseFloat(inputsToFill[0].value)
    const endValue = parseFloat(inputsToFill[inputsToFill.length - 1].value)
    if (isNaN(startValue) || isNaN(endValue))
      return this.#showPopup(this.MISSING_VALUES_ERROR_MSG_INDEX)

    const step = (endValue - startValue) / (inputsToFill.length - 1)

    for (let i = 1; i < inputsToFill.length - 1; i++) {
      const newValue = startValue + step * i
      const inputToFill = inputsToFill[i]
      if (inputToFill.hasAttribute('data-dummy')) continue

      this.#setInputValue(
        inputToFill,
        setDecimals(newValue, this.decimalCountValue)
      )
    }
  }

  #fillInputs(pastedTable, startPos) {
    let [rowIndex, cellIndex] = startPos
    pastedTable.querySelectorAll('tr').forEach((row) => {
      cellIndex = startPos[1] // reset cell position

      row.querySelectorAll('td').forEach((cell) => {
        const input = this.inputs[rowIndex]?.at(cellIndex) // safely access potentially undefined input, to not raise error so we can return in next line
        if (!input) return

        this.#setInputValue(input, cell.textContent)
        cellIndex++
      })

      rowIndex++
    })
  }

  #duplicateFirstValue() {
    const firstValue = this.nonDummySelectedInputs.find(
      (input) => input.value
    ).value
    this.selectedInputs.forEach((input) =>
      this.#setInputValue(input, firstValue)
    )
  }

  // returns array split into multiple of given chunk size
  #splitArray(array, chunkSize) {
    if (chunkSize < 1) throw new Error('chunkSize cannot be lower than 1')

    const result = []
    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize))
    }
    return result
  }

  #showPopup(index) {
    this.popupOutlets[index].show()
  }

  get inputs() {
    if (this._inputs) return this._inputs
    return (this._inputs = this.isQuartersEnabledValue
      ? this.#quarterlyInputs
      : this.#hourlyInputs)
  }

  get locks() {
    if (this._locks) return this._locks
    this._locks = this.isQuartersEnabledValue
      ? this.#quarterlyLocks
      : this.#hourlyLocks
    return this._locks
  }

  get lockIcons() {
    if (this._lockIcons) return this._lockIcons
    this._lockIcons = this.isQuartersEnabledValue
      ? this.#quarterlyLockIcons
      : this.#hourlyLockIcons
    return this._lockIcons
  }
  get nonDummySelectedInputs() {
    return this.selectedInputs.filter(
      (input) => !input.hasAttribute('data-dummy')
    )
  }

  get selectedTable() {
    const rowIndices = this.selectedInputs.map((input) =>
      input.getAttribute(this.ROW_INDEX_ATTR)
    )
    const cellIndices = this.selectedInputs.map((input) =>
      input.getAttribute(this.CELL_INDEX_ATTR)
    )

    const startRowIndex = Math.min(...rowIndices)
    const endRowIndex = Math.max(...rowIndices)
    const startCellIndex = Math.min(...cellIndices)
    const endCellIndex = Math.max(...cellIndices)

    const table = document.createElement('table')

    for (let i = startRowIndex; i <= endRowIndex; i++) {
      const row = document.createElement('tr')

      for (let j = startCellIndex; j <= endCellIndex; j++) {
        const cell = document.createElement('td')
        const inputEl = this.inputs[i][j]
        const inputIsSelected = inputEl.getAttribute('selected') === 'true'
        const inputIsDummy = inputEl.hasAttribute('data-dummy')

        cell.textContent = inputIsSelected && !inputIsDummy ? inputEl.value : ''
        row.appendChild(cell)
      }

      table.appendChild(row)
    }

    return table
  }

  // shorter, more readable aliases
  get hoursOutlets() {
    return this.kaiaManualForecastTableComponentHoursOutlets
  }
  get toolbarOutlet() {
    return this.kaiaManualForecastToolbarComponentOutlet
  }
  get popupOutlets() {
    return this.kaiaManualForecastPopupComponentOutlets
  }

  // returns 2D array of hourly inputs
  get #hourlyInputs() {
    return this.hoursOutlets.map((hoursOutlet) => hoursOutlet.hourVolumeTargets)
  }

  get #hourlyLocks() {
    return this.hoursOutlets.map((hoursOutlet) => hoursOutlet.hourLockTargets)
  }
  get #hourlyLockIcons() {
    const hourlyLockIcons = []
    this.hoursOutlets.forEach((hoursOutlet) => {
      hourlyLockIcons.push(
        ...this.#splitArray(
          hoursOutlet.hourLockIconTargets,
          hoursOutlet.rowLength
        )
      )
    })
    return hourlyLockIcons
  }
  // returns 2D array of quarterly inputs
  get #quarterlyInputs() {
    const quarterlyInputs = []
    this.hoursOutlets.forEach((hoursOutlet) => {
      quarterlyInputs.push(
        ...this.#splitArray(
          hoursOutlet.quarterVolumeTargets,
          hoursOutlet.rowLength
        )
      )
    })
    return quarterlyInputs
  }

  get #quarterlyLocks() {
    const quarterlyLocks = []
    this.hoursOutlets.forEach((hoursOutlet) => {
      quarterlyLocks.push(
        ...this.#splitArray(
          hoursOutlet.quarterLockTargets,
          hoursOutlet.rowLength
        )
      )
    })
    return quarterlyLocks
  }
  get #quarterlyLockIcons() {
    const quarterlyIcons = []
    this.hoursOutlets.forEach((hoursOutlet) => {
      quarterlyIcons.push(
        ...this.#splitArray(
          hoursOutlet.quarterLockIconTargets,
          hoursOutlet.rowLength
        )
      )
    })
    return quarterlyIcons
  }

  // returns whether inputs are chronologically selected without gaps
  get #hasChronologicallySelectedInputs() {
    let prevRowIndex = parseInt(
      this.selectedInputs[0].getAttribute(this.ROW_INDEX_ATTR)
    )
    let prevCellIndex = parseInt(
      this.selectedInputs[0].getAttribute(this.CELL_INDEX_ATTR)
    )
    const lastRowIndex = this.isQuartersEnabledValue
      ? this.lastQuarterRowIndexValue
      : this.lastHourRowIndexValue

    for (let i = 1; i < this.selectedInputs.length; i++) {
      // start iterating from second selected input
      const rowIndex = parseInt(
        this.selectedInputs[i].getAttribute(this.ROW_INDEX_ATTR)
      )

      if (this.selectedInputs[i].hasAttribute('data-dummy')) {
        // ignore invisible inputs
        prevRowIndex = rowIndex // update to make expectedRowIndex correct for next input
        continue
      }

      const cellIndex = parseInt(
        this.selectedInputs[i].getAttribute(this.CELL_INDEX_ATTR)
      )

      const prevInputWasLastForDate = prevRowIndex === lastRowIndex
      const isIteratingNewDate = cellIndex !== prevCellIndex

      const expectedRowIndex =
        prevInputWasLastForDate && isIteratingNewDate ? 0 : prevRowIndex + 1
      const expectedCellIndex =
        prevInputWasLastForDate && isIteratingNewDate
          ? prevCellIndex + 1
          : prevCellIndex
      if (rowIndex !== expectedRowIndex || cellIndex !== expectedCellIndex)
        return false

      ;[prevRowIndex, prevCellIndex] = [rowIndex, cellIndex]
    }

    return true
  }
}
